"use client";

import * as React from "react";
import { cn } from "./cn";

export type OtpInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  className?: string;
};

export function OtpInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled,
  invalid,
  autoFocus,
  className,
}: OtpInputProps) {
  const refs = React.useRef<(HTMLInputElement | null)[]>(
    Array(length).fill(null),
  );
  const digits = Array.from({ length }, (_, i) => value[i] ?? "");

  React.useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const emit = (next: string) => {
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };

  const handleChange = (idx: number, raw: string) => {
    const char = raw.replace(/\D/g, "").slice(-1);
    if (!char) return;
    const next = (
      digits
        .slice(0, idx)
        .concat(char)
        .concat(digits.slice(idx + 1))
        .join("") + ""
    ).slice(0, length);
    emit(next);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        const next = digits
          .slice(0, idx)
          .concat("")
          .concat(digits.slice(idx + 1))
          .join("");
        emit(next);
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
        const next = digits
          .slice(0, idx - 1)
          .concat("")
          .concat(digits.slice(idx))
          .join("");
        emit(next);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    if (text.length === 0) return;
    e.preventDefault();
    emit(text.padEnd(Math.min(text.length, length), ""));
    refs.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <div className={cn("flex gap-2", className)} onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.currentTarget.select()}
          className={cn(
            "min-w-0 flex-1 h-14 rounded-xl border-[1.5px] text-center text-2xl font-black text-ink bg-mist outline-none transition-all",
            "focus:border-lime focus:bg-paper",
            d && "border-ink bg-paper",
            !d && "border-smoke",
            invalid && "border-danger bg-danger/5",
            disabled && "opacity-60 pointer-events-none",
          )}
        />
      ))}
    </div>
  );
}
